<template>
  <Popover ref="itemPopover" :showHeader="true" :headerTitle="`${itemTitle}`" sizeClass="popover-xxlarge" :scrollable="true">
    <div class="production-summary-popover">
      <LoadingPanel :loading="loading" :loadingError="loadingError" />
      <div v-if="order && !loading">
        <DataPageArea title="Order">
          <template slot="dataArea">
            <DataItem title="Customer" v-if="order.contact">{{order.contact.name}}</DataItem>
            <DataItem title="Contact Person" v-if="order.contactPerson">
              {{order.contactPerson.firstName}} {{order.contactPerson.lastName}}
              <div v-if="order.contactPerson.email">{{order.contactPerson.email}}</div>
              <div v-if="order.contactPerson.mobile">{{order.contactPerson.mobile}}</div>
              <div v-if="order.contactPerson.phone">{{order.contactPerson.phone}}</div>
            </DataItem>

            <DataItem title="Custom Contact Person" v-if="!order.contactPerson && order.customContactName">
              {{order.customContactName}} <span v-if="order.customContactEmail">-</span> {{order.customContactEmail}}
              <div v-if="order.customContactMobile">{{order.customContactMobile}}</div>
              <div v-if="order.customContactDetails">
                <span v-html="convertNewlinesToBreaks(order.customContactDetails)" />
              </div>
            </DataItem>
            <DataItem title="Despatch Type">{{order.despatchTaskType.name}}</DataItem>
            
            <DataItem title="Despatch Address" v-if="isDespatchTaskTypeLocationBased">
              <LabelTags :labelList="addressDeliveryLabelList" />
              <AddressDisplay
                :address1="order.deliveryAddress1"
                :address2="order.deliveryAddress2"
                :suburb="order.deliverySuburb"
                :postcode="order.deliveryPostcode"
                :state="order.deliveryState"
                :country="order.deliveryCountry"
                />
            </DataItem>
            <DataItem title="Despatch Instructions">{{order.deliveryInstructions}}</DataItem>
            <DataItem title="Notes">{{order.notes}}</DataItem>
          </template>
        </DataPageArea>

        <FormHorizontalRule />
        <ContentTitle>Order Items</ContentTitle>
        <StaticDataTable class="static-table-list" variant="table-sm">
        <template v-slot:header>
           <tr>
              <th>Item</th>
              <th>Size</th>
              <th>Info</th>
              <th>Status</th>
            </tr>
        </template>
        <template v-slot:body>
          <tr v-for="orderItem of orderItemList" :key="orderItem.id">
            <td>{{orderItem.quantity}}x {{orderItem.stock.name}}</td>
            <td>{{orderItem.glassHeight}} x {{orderItem.glassWidth}}</td>
            <td>{{formatHyphenEmptyValue(orderItem.generatedDescription)}}</td>
            <td v-if="!isOrderItemStock(orderItem)" 
              :class="{ 'production-completed': orderItem.isCompletedProduction }">
              <i class="far fa-circle production-pending mr-2" v-if="!orderItem.isProductionStarted && !orderItem.isPartiallyCompleted && !orderItem.isCompletedProduction" />
              <i class="far fa-circle production-progress mr-2" v-if="orderItem.isProductionStarted && !orderItem.isPartiallyCompleted && !orderItem.isCompletedProduction" />
              <i class="fas fa-circle production-progress mr-2" v-if="orderItem.isPartiallyCompleted" />
              <i class="fas fa-check-circle production-completed mr-2" v-if="orderItem.isCompletedProduction" />
              {{orderItem.panelsCompletedProduction}} / {{orderItem.quantity}} Panels Ready
            </td>
            <td v-if="isOrderItemStock(orderItem)">Stock</td>
          </tr>
        </template>
        </StaticDataTable>
      </div>
    </div>
  </Popover>
</template>

<style lang="scss" scoped>
$completed-colour: #3EB240;
$waiting-colour: #487eb0;
$progress-colour: #FF8D3B;

.production-summary-popover {
  padding: 10px;
  font-size: 0.8rem;
  line-height: 1.1rem;
  min-width: 400px;
  min-height: 200px;
}
.production-pending {
  color: $waiting-colour;
}
.production-progress {
  color: $progress-colour;
}
.production-completed {
  color: $completed-colour;
}
</style>

<script>
import _ from 'lodash'
import FormMixin from '@/mixins/form'
import Popover from '@/components/popover.vue'

import { InventoryStockTypes, ProductionPanelStatusTypes } from '@/store/types'

export default {
  name: 'OrderProductionSummaryPopover',
  mixins: [FormMixin],
  components: {
    Popover
  },
  computed: {
    itemTitle() {
      if(!this.order)
        return ''
      return `Order ${this.order.sequenceNumber || ''}`
    },
    isDespatchTaskTypeLocationBased() {
      return this.order.despatchTaskType.isLocationTrackingEnabled
    }
  },
  data() {
    return {      
      loading: false,
      loadingError: '',
      orderId: null,
      order: null,
      orderPanelList: [],
      orderItemList: []
    }
  },
  created() {

  },
  methods: {
    displayFromElement(element, orderId) {
      this.orderId = orderId
      this.requestData()

      this.$refs.itemPopover.displayFromElement(element)
    },
    requestData() {
      this.loading = true
      this.loadingError = ''

      Promise.all([
        this.$apiService.ordersService.getOrder(this.orderId),
        this.$apiService.ordersService.getOrderProductionPanelList(this.orderId)
      ])
      .then(results => {
        this.order = results[0].data
        this.orderPanelList = results[1].data

        // Generate order item list & panel status
        this.orderItemList = this.order.orderItems
        .filter(orderItem => {
          if(orderItem.stockType != InventoryStockTypes.glass && orderItem.stockType != InventoryStockTypes.template && orderItem.stockType != InventoryStockTypes.assembly)
            return false
          if(orderItem.isCancelled)
            return false
          return true
        })
        .map(orderItem => {
          var outputItem = {
            id: orderItem.id,
            stockType: orderItem.stockType,
            quantity: orderItem.quantity,
            stock: orderItem.stock,
            glassWidth: orderItem.glassWidth,
            glassHeight: orderItem.glassHeight,
            description: orderItem.description || '',
            generatedDescription: orderItem.generatedDescription || '',
            mark: orderItem.mark,
            isProcessedItem: orderItem.isProcessedItem,
            panelsCompletedProduction: 0,
            panelsInProduction: 0,
            isProductionStarted: false,
            isPartiallyCompleted: false,
            isCompletedProduction: false
          }

          // Calculate panels that are in despatch/completed state
          outputItem.panelsCompletedProduction = this.orderPanelList.filter(panel => {
            if(panel.orderItemId != orderItem.id)
              return false
            return (panel.status == ProductionPanelStatusTypes.despatch || panel.status == ProductionPanelStatusTypes.completed)
          }).length

          outputItem.panelsInProduction = this.orderPanelList.filter(panel => {
            if(panel.orderItemId != orderItem.id)
              return false
            return (panel.status == ProductionPanelStatusTypes.production)
          }).length

          outputItem.isPartiallyCompleted = outputItem.panelsCompletedProduction > 0 && outputItem.panelsCompletedProduction < outputItem.quantity-1
          outputItem.isCompletedProduction = outputItem.panelsCompletedProduction == outputItem.quantity

          outputItem.isProductionStarted = outputItem.panelsInProduction > 0

          return outputItem
        })

      })
      .catch(err => {
        this.loadingError = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
        this.loading = false
      })      
    },
    isOrderItemStock(orderItem) {
      return !orderItem.isProcessedItem && orderItem.stockType != InventoryStockTypes.assembly
    }
  }
}
</script>